import type { ImageParsedTrack } from '../model/track.ts';
import { parseClockTime, parsePixelPair } from './clock.ts';

type ImageParagraph = {
  begin: string;
  end: string;
  attrs: string;
  innerHtml: string;
};

function parseAttributeMap(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  for (const match of attrString.matchAll(/\b([\w:]+)="([^"]*)"/g)) {
    if (match[1]) attrs[match[1]] = match[2] ?? '';
  }
  return attrs;
}

function findImageParagraphs(xml: string): ImageParagraph[] {
  const paragraphs: ImageParagraph[] = [];
  // Prefer <p> / namespaced <tt:p>; also accept any timed container with begin+end
  // (string fallback when callers lack a Document — live zip path uses DOM in legacyBridge).
  const regex = /<((?:[\w.-]+:)?p)\b([^>]*)>([\s\S]*?)<\/\1>/gi;
  for (const match of xml.matchAll(regex)) {
    const attrs = parseAttributeMap(match[2] ?? '');
    if (!attrs.begin || !attrs.end) continue;
    paragraphs.push({
      begin: attrs.begin,
      end: attrs.end,
      attrs: match[2] ?? '',
      innerHtml: match[3] ?? '',
    });
  }
  if (paragraphs.length > 0) return paragraphs;

  const anyTimed = /<([\w.-]+)\b([^>]*\bbegin="[^"]*"[^>]*)>([\s\S]*?)<\/\1>/gi;
  for (const match of xml.matchAll(anyTimed)) {
    const attrs = parseAttributeMap(match[2] ?? '');
    if (!attrs.begin || !attrs.end) continue;
    paragraphs.push({
      begin: attrs.begin,
      end: attrs.end,
      attrs: match[2] ?? '',
      innerHtml: match[3] ?? '',
    });
  }
  return paragraphs;
}

function readNamespacedExtentOrigin(attrs: string): {
  extent: { h: number; v: number } | null;
  origin: { h: number; v: number } | null;
} {
  // Ref uses getAttributeNS(ttml#styling, 'extent'|'origin'); serialized TTML
  // stores the same values as tts:extent / tts:origin on the <p> attrs string.
  const map = parseAttributeMap(attrs);
  return {
    extent: parsePixelPair(map['tts:extent'] ?? map.extent),
    origin: parsePixelPair(map['tts:origin'] ?? map.origin),
  };
}

function readImageSrc(innerHtml: string): string | undefined {
  const match = /<(?:[\w.-]+:)?image\b[^>]*\bsrc="([^"]*)"/i.exec(innerHtml);
  return match?.[1];
}

function readGlobalExtent(xml: string): { h: number; v: number } | null {
  const match = /<tt\b[^>]*\btts:extent="([^"]*)"/i.exec(xml);
  return parsePixelPair(match?.[1]);
}

/**
 * Pre-unzipped image TTML manifest + filename→URL map.
 * Mirrors functions/netflix.js parseImageBasedTrack (without zip I/O).
 */
export function parseImageTtml(xml: string, images: Record<string, string>): ImageParsedTrack {
  const tracks: ImageParsedTrack['tracks'] = [];

  for (const p of findImageParagraphs(xml)) {
    const srcKey = readImageSrc(p.innerHtml);
    const { extent, origin } = readNamespacedExtentOrigin(p.attrs);
    tracks.push({
      begin: parseClockTime(p.begin),
      end: parseClockTime(p.end),
      src: srcKey ? images[srcKey] : undefined,
      extent,
      origin,
    });
  }

  return {
    type: 'image',
    tracks,
    extent: readGlobalExtent(xml),
  };
}
